'use client';

export interface MemberFocusViewportSnapshot {
  left: number;
  top: number;
  width: number;
  height: number;
  scale: number;
}

export interface MemberFocusResult {
  viewport: MemberFocusViewportSnapshot;
  controlRect: Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom' | 'width' | 'height'>;
  clippedDistance: { left: number; top: number; right: number; bottom: number };
  scrollDelta: { x: number; y: number };
}

export interface MemberFocusOptions {
  /** No-event fallback for browsers that do not emit VisualViewport changes. */
  fallbackFrames?: number;
  /** Hard animation-frame bound for a noisy or continuously changing viewport. */
  maxFrames?: number;
  onSettled?: (result: MemberFocusResult) => void;
}

const DEFAULT_FALLBACK_FRAMES = 20;
const DEFAULT_MAX_FRAMES = 60;
const REQUIRED_QUIET_FRAMES = 2;
const MAX_SETTLE_WAIT_MS = 1_000;

function readViewport(): MemberFocusViewportSnapshot {
  const viewport = window.visualViewport;
  if (viewport) {
    return {
      left: viewport.offsetLeft,
      top: viewport.offsetTop,
      width: viewport.width,
      height: viewport.height,
      scale: viewport.scale,
    };
  }
  return { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight, scale: 1 };
}

function sameViewport(a: MemberFocusViewportSnapshot, b: MemberFocusViewportSnapshot): boolean {
  return (
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height &&
    a.scale === b.scale
  );
}

function nearestEdgeDelta(before: number, after: number): number {
  if (before <= 0) return after > 0 ? after : 0;
  if (after <= 0) return -before;
  return before <= after ? -before : after;
}

/**
 * Focus a member text control without Mobile Safari's default page jump, then
 * wait for the keyboard-driven visual viewport to settle before revealing only
 * a clipped edge. It never changes page scale, viewport metadata, or body
 * scrolling, so intentional pinch zoom remains browser-owned.
 *
 * The returned cleanup must run when the question, mode, or mount changes.
 */
export function focusMemberControl(
  control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
  options: MemberFocusOptions = {},
): () => void {
  if (typeof window === 'undefined' || !control.isConnected) return () => undefined;

  const fallbackFrames = Math.max(1, options.fallbackFrames ?? DEFAULT_FALLBACK_FRAMES);
  const maxFrames = Math.max(fallbackFrames, options.maxFrames ?? DEFAULT_MAX_FRAMES);
  const visualViewport = window.visualViewport;
  let cancelled = false;
  let settled = false;
  let animationFrame = 0;
  let timeout = 0;
  let frameCount = 0;
  let quietFrames = 0;
  let viewportChanged = false;
  let lastViewport = readViewport();

  const removeListeners = () => {
    visualViewport?.removeEventListener('resize', noteViewportChange);
    visualViewport?.removeEventListener('scroll', noteViewportChange);
    document.removeEventListener('pointerdown', cancel, true);
    document.removeEventListener('touchstart', cancel, true);
  };

  const cancel = () => {
    if (cancelled || settled) return;
    cancelled = true;
    if (animationFrame) cancelAnimationFrame(animationFrame);
    if (timeout) window.clearTimeout(timeout);
    removeListeners();
  };

  const reveal = () => {
    if (cancelled || settled) return;
    settled = true;
    if (animationFrame) cancelAnimationFrame(animationFrame);
    if (timeout) window.clearTimeout(timeout);
    removeListeners();
    if (!control.isConnected || document.activeElement !== control) return;

    const viewport = readViewport();
    const rect = control.getBoundingClientRect();
    const availableLeft = viewport.left;
    const availableTop = viewport.top;
    const availableRight = viewport.left + viewport.width;
    const availableBottom = viewport.top + viewport.height;
    const clippedDistance = {
      left: Math.max(0, availableLeft - rect.left),
      top: Math.max(0, availableTop - rect.top),
      right: Math.max(0, rect.right - availableRight),
      bottom: Math.max(0, rect.bottom - availableBottom),
    };
    const scrollDelta = {
      x: nearestEdgeDelta(clippedDistance.left, clippedDistance.right),
      y: nearestEdgeDelta(clippedDistance.top, clippedDistance.bottom),
    };

    if (scrollDelta.x !== 0 || scrollDelta.y !== 0) {
      window.scrollBy({
        behavior: 'auto',
        left: scrollDelta.x,
        top: scrollDelta.y,
      });
    }
    options.onSettled?.({
      viewport,
      controlRect: {
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      },
      clippedDistance,
      scrollDelta,
    });
  };

  const tick = () => {
    if (cancelled || settled) return;
    frameCount += 1;
    const viewport = readViewport();
    if (sameViewport(lastViewport, viewport)) {
      quietFrames += 1;
    } else {
      viewportChanged = true;
      quietFrames = 0;
      lastViewport = viewport;
    }
    const viewportSettled = viewportChanged && quietFrames >= REQUIRED_QUIET_FRAMES;
    const noEventFallback = !viewportChanged && frameCount >= fallbackFrames;
    if (viewportSettled || noEventFallback || frameCount >= maxFrames) {
      reveal();
      return;
    }
    animationFrame = requestAnimationFrame(tick);
  };

  function noteViewportChange() {
    viewportChanged = true;
    quietFrames = 0;
    lastViewport = readViewport();
  }

  visualViewport?.addEventListener('resize', noteViewportChange);
  visualViewport?.addEventListener('scroll', noteViewportChange);
  document.addEventListener('pointerdown', cancel, true);
  document.addEventListener('touchstart', cancel, { capture: true, passive: true });

  control.focus({ preventScroll: true });
  animationFrame = requestAnimationFrame(tick);
  timeout = window.setTimeout(reveal, MAX_SETTLE_WAIT_MS);
  return cancel;
}
